import React, { useState } from 'react';

import { Alert } from 'antd';
import Axios from 'axios';
import { Formik } from 'formik';

import styled from 'styled-components';

import { isJobEvent } from '@libs/EventTypes';
import { getMoment } from '@libs/NwMoment';
import { showDate, toISODate } from '@libs/NwMoment';

import { useDeleteEventDate } from '@hooks/API/Event/UseEvent';

import CustomDatePicker from '@components/EventDateTimePicker/CustomDatePicker';
import CustomSwitch from '@components/Gui/CustomSwitch';
import { NwButton, NwCancelButton, NwRemoveButton, NwSaveButton } from '@components/Gui/NwButton';
import { NwForm, NwFormButtonsBar } from '@components/Gui/NwForm/NwFormWrapper';

import SelectModelsIds from './SelectModelsIds';

const Container = styled.div`
    padding: 16px;
    .ant-legacy-form-item {
        margin-bottom: 16px;
        .ant-legacy-form-item-label {
            line-height: 1.2rem;
        }
    }
    .date-title {
        padding-bottom: 10px;
    }
`;

const DatePickerContainer = styled.div`
    display: flex;
    justify-content: space-between;
    padding-bottom: 40px;
`;

const SwitchContainer = styled.div`
    padding-bottom: 20px;
`;

const onMoveEvent = (data) => Axios.put(`/eventdates/Move`, data);

const MoveOrRemoveEventDate = ({ onClose, onSave, date, eventData, onTouchForm }) => {
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [showForceDelete, setShowForceDelete] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const { mutateAsync: onDeleteEventDate } = useDeleteEventDate();

    const initialValues = {
        modelIds: date.EventDatesModels.map((eventDateModel) => eventDateModel.ModelID),
        newDate: getMoment(date.FromDate),
        keepOptionNumber: false,
    };

    const onChangeDate = (form, date) => {
        form.setFieldValue('newDate', date);
    };

    const onSubmit = async (values) => {
        const moveEventData = {
            eventDateID: date.ID,
            newDate: showDate(values.newDate, 'short'),
            ...values,
        };

        if (isJobEvent(eventData.TypeName)) {
            moveEventData.keepOptionNumber = values.keepOptionNumber;
        }

        await onMoveEvent(moveEventData);
        onTouchForm(false);
        onSave();
    };

    const handleRemoveEventDates = async () => {
        try {
            await onDeleteEventDate({ ID: eventData.ID, eventDateId: date.ID });
            onSave();
        } catch (error) {
            console.error('error', error);
            if (error.errorCode === 'DateWithTransactions') {
                setShowForceDelete(true);
            } else {
                setDeleteError(error);
            }
        }
    };

    const handleForceDelete = async () => {
        setShowForceDelete(false);
        try {
            await Axios.delete(`/eventdates/${date.ID}?force=true`);
            onSave();
        } catch (error) {
            setDeleteError(error);
        }
    };

    const disableDate = (rangeDate) => {
        const period = eventData.EventDates.find((eventDate) => {
            if (
                toISODate(rangeDate) >= toISODate(eventDate.FromDate) &&
                toISODate(rangeDate) <= toISODate(eventDate.ToDate)
            ) {
                return true;
            }
            return false;
        });

        if (period) {
            return true;
        }
    };

    return (
        <>
            <Container>
                <Formik initialValues={initialValues} onSubmit={onSubmit}>
                    {(form) => (
                        <NwForm
                            values={form.values}
                            onTouchForm={onTouchForm}
                            onFinish={form.handleSubmit}
                            layout="vertical"
                        >
                            <div className="date-title">Move to date</div>
                            <DatePickerContainer>
                                <CustomDatePicker
                                    open={isDatePickerOpen}
                                    onOpenChange={setIsDatePickerOpen}
                                    label="Select day"
                                    name="newDate"
                                    value={form.values.newDate}
                                    onChange={(value) => onChangeDate(form, value)}
                                    disabledRange={disableDate}
                                />
                                <NwRemoveButton
                                    size="small"
                                    confirmationPlacement="bottomLeft"
                                    onClick={handleRemoveEventDates}
                                    label="Remove date"
                                    ghost
                                />
                            </DatePickerContainer>
                            {isJobEvent(eventData.TypeName) && (
                                <SwitchContainer>
                                    <CustomSwitch
                                        value={form.values.keepOptionNumber}
                                        onChange={(value) => form.setFieldValue('keepOptionNumber', value)}
                                        text="Keep option number"
                                    />
                                </SwitchContainer>
                            )}
                            {showForceDelete && (
                                <Alert
                                    message="Date has transactions"
                                    description="This date has linked transactions. Click the button on the right to force delete the date (the transactions will be deleted)."
                                    type="warning"
                                    showIcon
                                    action={
                                        <NwButton danger primary onClick={handleForceDelete} label="Force delete" />
                                    }
                                />
                            )}
                            {deleteError && (
                                <Alert
                                    message="Error deleting date"
                                    description={deleteError.message}
                                    type="error"
                                    showIcon
                                />
                            )}
                            <SelectModelsIds form={form} eventDates={[date]} onlyOneDay modelProperty="ModelID" />
                            <NwFormButtonsBar
                                left={
                                    <NwCancelButton
                                        disabled={form.isSubmitting}
                                        onClick={() => {
                                            onTouchForm(false);
                                            onClose();
                                        }}
                                    />
                                }
                                right={
                                    <NwSaveButton
                                        disabled={
                                            form.isSubmitting ||
                                            showDate(getMoment(date.FromDate), 'short') ===
                                                showDate(form.values.newDate, 'short')
                                        }
                                        loading={form.isSubmitting}
                                        label="Save"
                                        loadingLabel="Updating..."
                                    />
                                }
                            />
                        </NwForm>
                    )}
                </Formik>
            </Container>
        </>
    );
};

// EditEventDayForm.propTypes = {
//   onClose: PropTypes.func.isRequired,
//   onSave: PropTypes.func.isRequired,
//   date: PropTypes.shape({
//     FromDate: PropTypes.string,
//     ToDate: PropTypes.string,
//     EventAddressID: PropTypes.number,
//     models: PropTypes.arrayOf(PropTypes.number),
//   }).isRequired,
//   eventData: PropTypes.shape({
//     ID: PropTypes.number.isRequired
//   }).isRequired
// }

export default MoveOrRemoveEventDate;
